// Общие части экранов плана: статус, карточка плановой, цели на день, формы, разгрузка.

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Badge, Button, ErrorText, Icon, Input, Modal, Select, Spinner, useToast } from '@/components/ui'
import { formatDate, formatDayMonth, formatNumber, formatSigned } from '@/lib/format'
import { todayIso } from '@/lib/periods'
import { useTemplates } from '@/pages/workouts/api'

import {
  planApi,
  usePlanMutation,
  usePrescription,
  type Deload,
  type PlanKind,
  type Planned,
  type PlanStatus,
} from './api'
import { CardioFactView, GarminPush } from './garmin'
import { SPORTS, STATUS_META, draftFrom, draftToItem, plannedTitle, type CardioForm, type PlanItemDraft } from './logic'

export function StatusBadge({ status }: { status: PlanStatus }) {
  const meta = STATUS_META[status]
  return <Badge tone={meta.tone}>{meta.label}</Badge>
}

export function Warnings({ items }: { items: string[] }) {
  if (!items.length) return null
  return (
    <ul className="mt-2 space-y-1">
      {items.map((w) => (
        <li key={w} className="flex items-start gap-1.5 text-sm text-warning">
          <Icon name="alert" size={16} className="mt-0.5 shrink-0" />
          <span className="min-w-0">{w}</span>
        </li>
      ))}
    </ul>
  )
}

function setsSummary(sets: { reps: number | null; weight_kg: number | null; duration_s: number | null }[]): string {
  if (!sets.length) return 'без целевых подходов'
  const first = sets[0]!
  const same = sets.every((s) => s.reps === first.reps && s.weight_kg === first.weight_kg)
  const one = (s: (typeof sets)[number]) => {
    const reps = s.reps !== null ? `${s.reps}` : s.duration_s ? `${s.duration_s} с` : '—'
    return s.weight_kg !== null ? `${reps} × ${formatNumber(s.weight_kg)} кг` : reps
  }
  return same ? `${sets.length} × ${one(first)}` : sets.map(one).join(', ')
}

/** Цели шаблона на день: прогрессия с подписью причины, предупреждения о травмах. */
export function PrescriptionView({ templateId, day }: { templateId: string; day: string }) {
  const query = usePrescription(templateId, day)
  if (query.isPending) return <Spinner size={18} />
  if (query.error) return <ErrorText error={query.error} />
  const data = query.data
  return (
    <div className="space-y-2">
      <Warnings items={data.warnings} />
      <ol className="space-y-1.5">
        {data.exercises.map((ex) => (
          <li key={ex.template_exercise_id} className="text-sm">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium text-fg">{ex.exercise_name}</span>
              <span className="tabular-nums text-muted">{setsSummary(ex.sets)}</span>
              {ex.weight_delta_kg ? (
                <Badge tone={ex.weight_delta_kg > 0 ? 'accent' : 'warning'}>
                  {formatSigned(ex.weight_delta_kg)} кг
                </Badge>
              ) : null}
            </div>
            {ex.reason ? <p className="text-xs text-muted">{ex.reason}</p> : null}
            {ex.pending_deload_id ? (
              <p className="text-xs text-warning">Есть предложение разгрузки — вес не меняется без твоего согласия</p>
            ) : null}
            {ex.injury_warning ? (
              <p className="flex items-center gap-1 text-xs text-warning">
                <Icon name="alert" size={14} /> {ex.injury_warning}
              </p>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  )
}

export function PlannedCard({ item, onMove }: { item: Planned; onMove: (item: Planned) => void }) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const skip = usePlanMutation((skipped: boolean) => planApi.skip(item.id, skipped))
  const remove = usePlanMutation(() => planApi.remove(item.id))
  const active = item.status === 'planned' || item.status === 'missed'
  const title = plannedTitle(item)
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Icon name={item.kind === 'cardio' ? 'activity' : 'workout'} size={18} className="shrink-0 text-muted" />
        <span className="min-w-0 flex-1 truncate font-medium text-fg">{title}</span>
        <StatusBadge status={item.status} />
      </div>
      {item.moved_from ? (
        <p className="mt-1 text-xs text-muted">Перенесено с {formatDayMonth(item.moved_from)}</p>
      ) : null}
      {item.rule_id === null ? <p className="mt-1 text-xs text-muted">Разовая</p> : null}
      <Warnings items={item.warnings} />
      <GarminPush item={item} />
      <CardioFactView item={item} />
      {open && item.template_id ? (
        <div className="mt-2 border-t border-border pt-2">
          <PrescriptionView templateId={item.template_id} day={item.day} />
        </div>
      ) : null}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {active && item.kind === 'strength' && item.template_id && item.day === todayIso() ? (
          <Link
            to={`/workout?planned=${item.id}`}
            className="inline-flex min-h-11 items-center rounded-lg bg-accent px-3 text-sm font-medium text-accent-fg hover:bg-accent-strong lg:min-h-8"
          >
            Открыть
          </Link>
        ) : null}
        {item.template_id && item.status !== 'done' ? (
          <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? 'Скрыть цели' : 'Цели на день'}
          </Button>
        ) : null}
        {item.status === 'done' && item.workout_session_id ? (
          <Link
            to={`/workouts/${item.workout_session_id}`}
            className="inline-flex min-h-11 items-center px-3 text-sm font-medium text-accent hover:underline lg:min-h-8"
          >
            Открыть тренировку
          </Link>
        ) : null}
        {active ? (
          <>
            <Button size="sm" onClick={() => onMove(item)}>
              Перенести
            </Button>
            <Button
              size="sm"
              loading={skip.isPending}
              onClick={() =>
                skip.mutate(true, { onSuccess: () => toast.show({ message: `Пропущено: ${title}` }) })
              }
            >
              Пропустить
            </Button>
          </>
        ) : null}
        {item.status === 'skipped' ? (
          <Button size="sm" loading={skip.isPending} onClick={() => skip.mutate(false)}>
            Вернуть в план
          </Button>
        ) : null}
        {item.rule_id === null && item.status !== 'done' ? (
          <Button size="sm" variant="ghost" loading={remove.isPending} onClick={() => remove.mutate(undefined)}>
            Удалить
          </Button>
        ) : null}
      </div>
      <ErrorText error={skip.error ?? remove.error} className="mt-1" />
    </div>
  )
}

export function MoveModal({ item, onClose }: { item: Planned | null; onClose: () => void }) {
  const [day, setDay] = useState('')
  const move = usePlanMutation((target: string) => planApi.move(item?.id ?? '', target))
  const toast = useToast()
  const value = day || item?.day || ''
  return (
    <Modal
      open={item !== null}
      onClose={() => {
        setDay('')
        move.reset()
        onClose()
      }}
      title="Перенести тренировку"
      description={item ? `${plannedTitle(item)}, ${formatDate(item.day)}` : undefined}
      footer={
        <Button
          variant="primary"
          loading={move.isPending}
          disabled={!value || value === item?.day}
          onClick={() =>
            move.mutate(value, {
              onSuccess: () => {
                toast.show({ message: `Перенесено на ${formatDate(value)}` })
                setDay('')
                onClose()
              },
            })
          }
        >
          Перенести
        </Button>
      }
    >
      <Input type="date" label="Новый день" min={todayIso()} value={value} onChange={(e) => setDay(e.target.value)} />
      <ErrorText error={move.error} className="mt-2" />
    </Modal>
  )
}

/** Поля кардио: вид, длительность или дистанция, пульсовая зона. */
export function CardioFields({ value, onChange }: { value: CardioForm; onChange: (v: CardioForm) => void }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <Select
        label="Вид"
        options={SPORTS}
        value={value.sport}
        onChange={(e) => onChange({ ...value, sport: e.target.value as CardioForm['sport'] })}
        containerClassName="col-span-2"
      />
      <Select
        label="Объём"
        options={[
          { value: 'time', label: 'Длительность' },
          { value: 'distance', label: 'Дистанция' },
        ]}
        value={value.mode}
        onChange={(e) => onChange({ ...value, mode: e.target.value as CardioForm['mode'] })}
      />
      <Input
        label={value.mode === 'time' ? 'Минут' : 'Километров'}
        inputMode="decimal"
        suffix={value.mode === 'time' ? 'мин' : 'км'}
        value={value.value}
        onChange={(e) => onChange({ ...value, value: e.target.value })}
      />
      <Select
        label="Пульсовая зона"
        placeholder="Без зоны"
        options={[1, 2, 3, 4, 5].map((z) => ({ value: String(z), label: `Зона ${z}` }))}
        value={value.zone}
        onChange={(e) => onChange({ ...value, zone: e.target.value })}
        containerClassName="col-span-2"
      />
    </div>
  )
}

/** Силовая по шаблону или кардио — общая часть формы правила и разовой тренировки. */
export function PlanItemFields({ value, onChange }: { value: PlanItemDraft; onChange: (v: PlanItemDraft) => void }) {
  const templates = useTemplates(false)
  return (
    <div className="space-y-3">
      <Select
        label="Что"
        options={[
          { value: 'strength', label: 'Силовая по шаблону' },
          { value: 'cardio', label: 'Кардио' },
        ]}
        value={value.kind}
        onChange={(e) => onChange({ ...value, kind: e.target.value as PlanKind })}
      />
      {value.kind === 'strength' ? (
        <Select
          label="Шаблон"
          placeholder={templates.isPending ? 'Загрузка…' : 'Выбери шаблон'}
          options={(templates.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
          value={value.template_id}
          onChange={(e) => onChange({ ...value, template_id: e.target.value })}
          hint={templates.data && !templates.data.length ? 'Сначала создай шаблон в разделе «Шаблоны»' : undefined}
        />
      ) : (
        <CardioFields value={value.cardio} onChange={(cardio) => onChange({ ...value, cardio })} />
      )}
    </div>
  )
}

export function AddPlannedModal({ open, day, onClose }: { open: boolean; day: string; onClose: () => void }) {
  const [draft, setDraft] = useState<PlanItemDraft>(draftFrom('strength', null, null))
  const [date, setDate] = useState('')
  const [error, setError] = useState<string | null>(null)
  const add = usePlanMutation(planApi.add)
  const toast = useToast()
  const target = date || day
  const close = () => {
    setDate('')
    setError(null)
    add.reset()
    onClose()
  }
  const submit = () => {
    const item = draftToItem(draft)
    if (!item.ok) {
      setError(item.error)
      return
    }
    setError(null)
    add.mutate(
      { day: target, kind: draft.kind, template_id: item.template_id, cardio: item.cardio },
      {
        onSuccess: () => {
          toast.show({ message: `Запланировано на ${formatDate(target)}` })
          close()
        },
      },
    )
  }
  return (
    <Modal
      open={open}
      onClose={close}
      title="Разовая тренировка"
      footer={
        <Button variant="primary" loading={add.isPending} onClick={submit}>
          Добавить
        </Button>
      }
    >
      <div className="space-y-3">
        <Input type="date" label="День" min={todayIso()} value={target} onChange={(e) => setDate(e.target.value)} />
        <PlanItemFields value={draft} onChange={setDraft} />
        <ErrorText error={add.error}>{error}</ErrorText>
      </div>
    </Modal>
  )
}

function DeloadRow({ item }: { item: Deload }) {
  const resolve = usePlanMutation((accept: boolean) => planApi.resolveDeload(item.id, accept))
  const toast = useToast()
  const done = (accept: boolean) =>
    resolve.mutate(accept, {
      onSuccess: () => toast.show({ message: accept ? 'Разгрузка принята — вес снижен' : 'Предложение отклонено' }),
    })
  return (
    <li className="rounded-lg border border-border bg-surface px-3 py-2.5">
      <p className="text-sm font-medium text-fg">
        {item.exercise_name}
        {item.template_name ? <span className="font-normal text-muted"> · {item.template_name}</span> : null}
      </p>
      <p className="mt-0.5 text-sm tabular-nums text-fg">
        {item.current_weight_kg !== null ? `${formatNumber(item.current_weight_kg)} → ` : ''}
        {formatNumber(item.suggested_weight_kg)} кг
      </p>
      {item.reason ? <p className="mt-0.5 text-xs text-muted">{item.reason}</p> : null}
      <div className="mt-2 flex gap-2">
        <Button size="sm" variant="primary" loading={resolve.isPending && resolve.variables} onClick={() => done(true)}>
          Принять
        </Button>
        <Button size="sm" loading={resolve.isPending && !resolve.variables} onClick={() => done(false)}>
          Отклонить
        </Button>
      </div>
      <ErrorText error={resolve.error} className="mt-1" />
    </li>
  )
}

export function DeloadList({ items }: { items: Deload[] }) {
  if (!items.length) return null
  return (
    <section aria-label="Предложения разгрузки" className="mb-4 rounded-xl border border-warning/40 bg-warning-soft p-3">
      <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-fg">
        <Icon name="alert" size={16} className="text-warning" /> Предложение разгрузки
      </h2>
      <ul className="space-y-2">
        {items.map((d) => (
          <DeloadRow key={d.id} item={d} />
        ))}
      </ul>
    </section>
  )
}
